import React, { useCallback, useEffect, useState } from "react";
import "../css/cabSummary.css";
import api from "../utils/axiosConfig";
import localforage from "localforage";
import ReactPaginate from "react-paginate";
import "../css/leaveRequestsAdmin.css";
import Dashboard from "./Dashboard";

const API_URL = process.env.REACT_APP_API_URL;

// Multi-leg helpers: use the arrays when present, else fall back to the legacy
// single fields so older records still render.
const tvFmtDateTime = (d) =>
  d
    ? new Date(d).toLocaleString("en-GB", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit",
        timeZone: "Asia/Kolkata",
      })
    : "NA";

// Expand a record into one entry per leg for the sheet-style table, pairing each
// leg with its accommodation (per-leg fields first, then the old accommodations[]
// list by index, then legacy single fields).
const tvExpandLegs = (t) => {
  const stays = Array.isArray(t.accommodations) ? t.accommodations : [];
  if (Array.isArray(t.travelLegs) && t.travelLegs.length) {
    return t.travelLegs.map((l, idx) => {
      const s = stays[idx] || null;
      return {
        fromLocation: l.fromLocation || "",
        toLocation: l.toLocation || "",
        travelDate: l.travelDate || null,
        accommodationPlace: l.accommodationPlace || (s && s.location) || "",
        accommodationStartDate: l.accommodationStartDate || (s && s.startDate) || null,
        accommodationEndDate: l.accommodationEndDate || (s && s.endDate) || null,
      };
    });
  }
  const s = stays[0] || null;
  return [
    {
      fromLocation: t.fromLocation || "",
      toLocation: t.toLocation || "",
      travelDate: t.travelDate || null,
      accommodationPlace: (s && s.location) || "",
      accommodationStartDate: (s && s.startDate) || t.accommodationStartDate || null,
      accommodationEndDate: (s && s.endDate) || t.accommodationEndDate || null,
    },
  ];
};



const TravelRequestAdmin = () => {
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [expandedRows, setExpandedRows] = useState({});
  const itemsPerPage = 10;

  const fetchLeaveRequests = async (email) => {
    try {
      const token = await localforage.getItem("token");
      const response = await api.get(
        `${API_URL}/travel/travel-requests-emails`,
        {
          params: { receiverEmail: email },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const updatedRequests = response.data.leaveRequests.map((request) => ({
        ...request,
        requestStatus: request.requestStatus || "pending",
      }));
      setLeaveRequests(updatedRequests);
      setFilteredRequests(updatedRequests);
    } catch (err) {
      setError("Error fetching travel requests");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getReceiverEmailFromLocalforage = useCallback(async () => {
    try {
      const email = await localforage.getItem("email");
      if (email) {
        fetchLeaveRequests(email);
      } else {
        setError("Receiver email not found in localforage.");
      }
    } catch (err) {
      setError("Error retrieving email from localforage");
      console.error(err);
    }
  }, []);

  useEffect(() => {
    getReceiverEmailFromLocalforage();
  }, [getReceiverEmailFromLocalforage]);

  const updateLeaveStatus = async (id, requestStatus) => {
    try {
      const token = await localforage.getItem("token");

      await api.put(
        `${API_URL}/travel/update-travel-status/${id}`,
        { requestStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setLeaveRequests((prev) =>
        prev.map((request) =>
          request._id === id ? { ...request, requestStatus } : request
        )
      );
      setFilteredRequests((prev) =>
        prev.map((request) =>
          request._id === id ? { ...request, requestStatus } : request
        )
      );
    } catch (err) {
      setError(err.response?.data?.error || "Error updating travel status");
      console.error(err);
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (query) {
      const filtered = leaveRequests.filter((request) => {
        const name = request.name ? request.name.toLowerCase() : "";
        const travelCode = request.travelCode
          ? request.travelCode.toLowerCase()
          : "";

        return (
          name.includes(query.toLowerCase()) ||
          travelCode.includes(query.toLowerCase())
        );
      });

      setFilteredRequests(filtered);
    } else {
      setFilteredRequests(leaveRequests);
    }
    setCurrentPage(0);
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;

  const formatLeaveType = (type) =>
    type.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase());

  const handlePageClick = (data) => {
    setCurrentPage(data.selected);
  };

  const toggleRowExpansion = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const pageCount = Math.ceil(filteredRequests.length / itemsPerPage);
  const displayedRequests = filteredRequests.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  const truncateText = (text, id) => {
    const words = text.split(" ");
    if (expandedRows[id] || words.length <= 5) {
      return (
        <>
          {text}{" "}
          {words.length > 5 && (
            <span
              className="toggle-text"
              onClick={() => toggleRowExpansion(id)}
            >
              Show Less
            </span>
          )}
        </>
      );
    }
    return (
      <>
        {words.slice(0, 5).join(" ")}...{" "}
        <span className="toggle-text" onClick={() => toggleRowExpansion(id)}>
          Show More
        </span>
      </>
    );
  };

  return (
    <>
      <Dashboard />
      <div>
        <h2>Travel Requests</h2>
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search by name or travel code..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>
        <div className="intervention-table">
          <table className="travel-admin-table">
            <thead>
              <tr>
                <th>S.N</th>
                <th>Team Member Name</th>
                <th>Phone Number</th>
                <th>Travel From</th>
                <th>Travel To</th>
                <th>Travel Date &amp; Time</th>
                <th>Accommodation Place</th>
                <th>Accommodation Start Date &amp; Time</th>
                <th>Accommodation End Date &amp; Time</th>
                <th>Department</th>
                <th>Travel Instructed By</th>
                <th>Event</th>
                <th>Request Type</th>
                <th>Purpose Of Travel</th>
                <th>Requested By</th>
                <th>Group</th>
                <th>Travel Status</th>
                <th>Summary</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                let sn = 0;
                return displayedRequests.map((travel) => {
                  const legs = tvExpandLegs(travel);
                  return legs.map((leg, li) => {
                    sn += 1;
                    return (
                      <tr key={travel._id + "-" + li}>
                        <td>{sn}</td>
                        <td>{travel.name}</td>
                        <td>{travel.employeePhoneNumber || "—"}</td>
                        <td>{leg.fromLocation || "—"}</td>
                        <td>{leg.toLocation || "—"}</td>
                        <td>{tvFmtDateTime(leg.travelDate)}</td>
                        <td>{leg.accommodationPlace || "—"}</td>
                        <td>{tvFmtDateTime(leg.accommodationStartDate)}</td>
                        <td>{tvFmtDateTime(leg.accommodationEndDate)}</td>
                        {li === 0 && (
                          <>
                            <td rowSpan={legs.length} style={{ textAlign: "center" }}>{travel.department}</td>
                            <td rowSpan={legs.length} style={{ textAlign: "center" }}>{travel.travelInstructedBy}</td>
                            <td rowSpan={legs.length} style={{ textAlign: "center", maxWidth: "180px" }}>
                              {travel.eventLocation} - {travel.eventName} -{" "}
                              {truncateText(travel.eventDetails || "", travel._id)}
                            </td>
                            <td rowSpan={legs.length}>{formatLeaveType(travel.requestType)}</td>
                            <td rowSpan={legs.length} style={{ minWidth: "140px", textAlign: "center" }}>{travel.purposeOfTravel}</td>
                            <td rowSpan={legs.length} style={{ textAlign: "center" }}>{travel.requestedByName || "—"}</td>
                            <td rowSpan={legs.length} style={{ textAlign: "center" }}>{travel.isGroupRequest ? "Group" : "Self"}</td>
                            <td
                              rowSpan={legs.length}
                              className={
                                travel.requestStatus === "approved"
                                  ? "status-approved"
                                  : travel.requestStatus === "not approved"
                                  ? "status-not-approved"
                                  : "status-pending"
                              }
                            >
                              <select
                                value={travel.requestStatus}
                                onChange={(e) =>
                                  updateLeaveStatus(travel._id, e.target.value)
                                }
                                style={{ width: "100px" }}
                              >
                                <option value="approved">Approved</option>
                                <option value="not approved">Not Approved</option>
                                <option value="pending">Pending</option>
                              </select>
                            </td>
                            <td rowSpan={legs.length} className="cab-summary">
                              <div className="cab-summary-card">
                                <div>
                                  <span className="cab-summary-name">{travel.name || "-"}</span>{" "}
                                  <span className="cab-summary-role">
                                    {travel.employeeCode || "-"}
                                    {travel.department ? ` · ${travel.department}` : ""}
                                  </span>
                                </div>
                                <div className="cab-summary-row">
                                  <span className="cab-summary-label">Phone</span>
                                  <span className="cab-summary-val">{travel.employeePhoneNumber || "-"}</span>
                                </div>
                                <div className="cab-summary-row">
                                  <span className="cab-summary-label">Request Type</span>
                                  <span className="cab-summary-val">{formatLeaveType(travel.requestType)}</span>
                                </div>
                                <div className="cab-summary-row">
                                  <span className="cab-summary-label">Purpose</span>
                                  <span className="cab-summary-val">{travel.purposeOfTravel || "-"}</span>
                                </div>
                                <div className="cab-summary-row">
                                  <span className="cab-summary-label">Requested By</span>
                                  <span className="cab-summary-val">{travel.requestedByName || "Self"}</span>
                                </div>
                                <div className="cab-summary-row">
                                  <span className="cab-summary-label">Group</span>
                                  <span className="cab-summary-val">{travel.isGroupRequest ? "Group" : "Self"}</span>
                                </div>
                                <div className="cab-summary-row">
                                  <span className="cab-summary-label">Travel Instructed By</span>
                                  <span className="cab-summary-val">{travel.travelInstructedBy || "-"}</span>
                                </div>
                                <div className="cab-summary-row">
                                  <span className="cab-summary-label">Event</span>
                                  <span className="cab-summary-val">{travel.eventName || "-"}</span>
                                </div>
                                <div className="cab-summary-row">
                                  <span className="cab-summary-label">Event Location</span>
                                  <span className="cab-summary-val">{travel.eventLocation || "-"}</span>
                                </div>
                                <div className="cab-summary-row">
                                  <span className="cab-summary-label">Event Details</span>
                                  <span className="cab-summary-val">{travel.eventDetails || "-"}</span>
                                </div>
                                <div className="cab-summary-row">
                                  <span className="cab-summary-label">Remarks</span>
                                  <span className="cab-summary-val">{travel.remarks || "-"}</span>
                                </div>
                                <div className="cab-summary-row">
                                  <span className="cab-summary-label">Travel Code</span>
                                  <span className="cab-summary-val">{travel.travelCode || "-"}</span>
                                </div>
                                <div className="cab-summary-row">
                                  <span className="cab-summary-label">Status</span>
                                  <span className="cab-summary-val">{travel.requestStatus || "-"}</span>
                                </div>
                                <div className="cab-summary-divider" />
                                <div style={{ fontWeight: 600, marginBottom: "4px" }}>
                                  Itinerary
                                </div>
                                {tvExpandLegs(travel).map((lg, x) => (
                                  <div className="cab-summary-row" key={x} style={{ display: "block" }}>
                                    <span className="cab-summary-label">Leg {x + 1}</span>
                                    <span className="cab-summary-val" style={{ display: "block" }}>
                                      {(lg.fromLocation || lg.toLocation)
                                        ? `${lg.fromLocation || "?"} → ${lg.toLocation || "?"} · ${tvFmtDateTime(lg.travelDate)}`
                                        : "No travel"}
                                      {lg.accommodationPlace
                                        ? ` | Stay: ${lg.accommodationPlace} (${tvFmtDateTime(lg.accommodationStartDate)} → ${tvFmtDateTime(lg.accommodationEndDate)})`
                                        : ""}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </td>
                          </>
                        )}
                      </tr>
                    );
                  });
                });
              })()}
            </tbody>
          </table>
        </div>
        <ReactPaginate
          pageCount={pageCount}
          pageRangeDisplayed={5}
          marginPagesDisplayed={2}
          previousLabel={"previous"}
          nextLabel={"next"}
          breakLabel={"..."}
          onPageChange={handlePageClick}
          containerClassName={"pagination"}
          subContainerClassName={"pages pagination"}
          activeClassName={"active"}
        />
      </div>
    </>
  );
};

export default TravelRequestAdmin;
